import React, { useEffect, useRef, useState } from "react";
import {
  Box, Typography, IconButton, Dialog, DialogContent, CircularProgress, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import API from "../API/API";
import { getImageUrl } from "../utils/imageHelper";

const api = new API();

// Customer Visits History form (opened from the top menu, all users): search a guest by name / phone,
// then every stay of the guest + the ID proofs of the selected (latest) stay, all in the same form.
// Same form as the main hotel app (Hotel_fe GuestHistorySearch), same backend: api/guests/search + history.

// Dark colours, set here (this app has no global dark dialog styling like the main app)
const DIALOG_BG = "#0e1729";
const TEAL = "#0e7c86";
const CYAN = "#00d7d7";
const LABEL = "#7BA8D5";
const MUTED = "#8faac8";
const PANEL = "#111c31";
const LINE = "#1c2b4d";

const headCell = {
  bgcolor: "#0b1120 !important", color: CYAN, fontWeight: "bold", fontSize: "12px",
  border: `1px solid ${LINE}`, whiteSpace: "nowrap", py: 0.8, px: 1, textAlign: "center",
};
const bodyCell = { color: "#fff", fontSize: "12px", border: `1px solid ${LINE}`, whiteSpace: "nowrap", py: 0.6, px: 1 };

const money = (v) => (parseFloat(v || 0) || 0).toFixed(2);

// Guest search input + suggestion list ("NAME - PHONE") + SHOW button. onPick(guest) when one is chosen.
function GuestSearchBox({ onPick, autoFocus = false }) {
  const [text, setText] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const reqId = useRef(0);

  useEffect(() => {
    const q = text.trim();
    if (q.length < 1) {
      setItems([]);
      setLoading(false);
      return undefined;
    }
    const id = ++reqId.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`api/guests/search?q=${encodeURIComponent(q)}`);
        if (id !== reqId.current) return; // a newer search is running
        const list = Array.isArray(res?.data) ? res.data : [];
        setItems(list);
        setActive(list.length ? 0 : -1);
      } catch (e) {
        if (id === reqId.current) setItems([]);
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [text]);

  // close the list on an outside click
  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const pick = (g) => {
    if (!g) return;
    setText(g.label);
    setOpen(false);
    onPick(g);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(items[active] || items[0]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: "13px", color: LABEL, fontWeight: "bold" }}>Guest Name / Phone No:</Typography>
      <Box ref={boxRef} sx={{ position: "relative", flex: 1, minWidth: 220, maxWidth: 420 }}>
        <Box sx={{
          display: "flex", alignItems: "center", height: "36px", px: 1, gap: 0.5,
          bgcolor: "#070E1A", border: "1px solid #3a4a6b", borderRadius: "4px",
          "&:focus-within": { borderColor: CYAN },
        }}>
          <SearchIcon sx={{ fontSize: 18, color: CYAN }} />
          <input
            value={text}
            autoFocus={autoFocus}
            placeholder="Type guest name or phone number"
            onChange={(e) => { setText(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            style={{ flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none", color: "#fff", fontSize: "14px", fontWeight: "bold" }}
          />
          {loading && <CircularProgress size={14} sx={{ color: CYAN }} />}
        </Box>
        {open && text.trim().length >= 1 && !loading && (
          <Box sx={{
            position: "absolute", top: "100%", left: 0, mt: "2px", zIndex: 1500, width: "100%",
            bgcolor: "#16243e", border: "1px solid #3a4a6b", borderRadius: "4px", boxShadow: 6, maxHeight: 300, overflowY: "auto",
          }}>
            {items.length === 0 ? (
              <Typography sx={{ p: 1.2, fontSize: "12px", color: MUTED }}>No guest found</Typography>
            ) : items.map((g, i) => (
              <Box
                key={`${g.phone_no}-${g.guest_name}-${i}`}
                onMouseDown={(e) => { e.preventDefault(); pick(g); }}
                onMouseEnter={() => setActive(i)}
                sx={{
                  px: 1.2, py: 0.7, cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 2,
                  bgcolor: i === active ? "#1f4e79" : "#16243e", borderBottom: "1px solid #22324f",
                }}
              >
                <Typography sx={{ fontSize: "12px", fontWeight: "bold", color: "#fff" }}>{g.label}</Typography>
                <Typography sx={{ fontSize: "11px", color: CYAN, whiteSpace: "nowrap" }}>
                  {g.visits} {g.visits === 1 ? "visit" : "visits"}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Button
        onClick={() => pick(items[active] || items[0])}
        disabled={!items.length}
        sx={{ bgcolor: "#2e7d32", color: "#fff", fontWeight: "bold", height: "36px", px: 2.5, "&:hover": { bgcolor: "#1b5e20" }, "&.Mui-disabled": { bgcolor: "#1f3a2a", color: "#7fa889" } }}
      >
        SHOW
      </Button>
    </Box>
  );
}

export default function CustomerVisitsHistory({ open, onClose }) {
  const fullScreen = useMediaQuery("(max-width:900px)");
  const [guest, setGuest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(0);
  const [preview, setPreview] = useState(null);

  // a fresh form every time it is opened
  useEffect(() => {
    if (open) {
      setGuest(null);
      setData(null);
      setSelected(0);
    }
  }, [open]);

  useEffect(() => {
    if (!guest?.phone_no) return undefined;
    let alive = true;
    setLoading(true);
    setData(null);
    setSelected(0);
    api.get(`api/guests/history?phone=${encodeURIComponent(guest.phone_no)}`)
      .then((res) => { if (alive) setData(res?.data || null); })
      .catch(() => { if (alive) setData({ visits: [], summary: {} }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [guest]);

  const visits = data?.visits || [];
  const summary = data?.summary || {};
  const current = visits[selected];

  return (
    <Dialog open={!!open} onClose={onClose} fullScreen={fullScreen} maxWidth="xl" fullWidth
      PaperProps={{ sx: {
        borderRadius: fullScreen ? 0 : "4px", overflow: "hidden", minHeight: fullScreen ? undefined : "60vh",
        bgcolor: DIALOG_BG, color: "#fff", border: fullScreen ? "none" : "1.5px solid #ffffff",
      } }}>
      <Box sx={{ bgcolor: TEAL, color: "#fff", px: 2, py: 1, display: "flex", alignItems: "center" }}>
        <Typography sx={{ fontSize: "17px", fontWeight: "bold", flex: 1 }}>Customer Visits History</Typography>
        <IconButton onClick={onClose} sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ p: { xs: 1.5, sm: 2 }, pt: "24px !important", bgcolor: DIALOG_BG, color: "#fff", display: "flex", flexDirection: "column", gap: 1.5 }}>
        <GuestSearchBox onPick={setGuest} autoFocus />

        {!guest ? (
          <Typography sx={{ fontSize: "13px", color: MUTED, py: 4, textAlign: "center" }}>
            Search a guest by name or phone number to see all visits and ID proofs.
          </Typography>
        ) : loading ? (
          <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress sx={{ color: TEAL }} /></Box>
        ) : (
          <>
            {/* Guest summary */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 1.5, sm: 3 }, alignItems: "baseline" }}>
              <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "#E040FB" }}>
                {data?.guest_name || guest.guest_name} - {data?.phone_no || guest.phone_no}
              </Typography>
              {[
                ["Total Visits", summary.total_visits ?? 0],
                ["Total Nights", summary.total_nights ?? 0],
                ["Total Amount", money(summary.total_amount)],
                ["Last Visit", summary.last_visit || "-"],
              ].map(([label, value]) => (
                <Typography key={label} sx={{ fontSize: "13px", color: LABEL }}>
                  {label}: <b style={{ color: "#fff" }}>{value}</b>
                </Typography>
              ))}
            </Box>

            {/* Visits */}
            <TableContainer sx={{ bgcolor: PANEL, border: `1px solid ${LINE}`, maxHeight: "45vh" }}>
              <Table size="small" stickyHeader sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    {["Room No", "Company Name", "Check In", "Check Out", "Visiting Purpose", "Booking Type", "Room Type",
                      "EP / CP", "Oneday Rate", "Nights", "Gross"].map((h) => <TableCell key={h} sx={headCell}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visits.length === 0 ? (
                    <TableRow><TableCell colSpan={11} sx={{ ...bodyCell, textAlign: "center", py: 3, color: MUTED }}>No visits found</TableCell></TableRow>
                  ) : visits.map((v, i) => (
                    <TableRow key={v.check_in_id} onClick={() => setSelected(i)}
                      sx={{
                        cursor: "pointer",
                        "&:hover td": { bgcolor: "rgba(0, 215, 215, 0.12)" },
                        "& td": i === selected ? { bgcolor: "#1f4e79 !important", fontWeight: "bold" } : { bgcolor: PANEL },
                      }}>
                      <TableCell sx={{ ...bodyCell, textAlign: "center" }}>
                        {v.room_no}
                        {v.is_checked_in && (
                          <Box component="span" sx={{ ml: 0.5, px: 0.5, fontSize: "9px", bgcolor: "#d32f2f", color: "#fff", borderRadius: "3px" }}>IN</Box>
                        )}
                      </TableCell>
                      <TableCell sx={bodyCell}>{v.company_name}</TableCell>
                      <TableCell sx={{ ...bodyCell, textAlign: "center" }}>{v.check_in}</TableCell>
                      <TableCell sx={{ ...bodyCell, textAlign: "center" }}>{v.check_out}</TableCell>
                      <TableCell sx={bodyCell}>{v.visiting_purpose}</TableCell>
                      <TableCell sx={bodyCell}>{v.booking_type}</TableCell>
                      <TableCell sx={bodyCell}>{v.room_type}</TableCell>
                      <TableCell sx={{ ...bodyCell, textAlign: "center" }}>{v.ep_cp}</TableCell>
                      <TableCell sx={{ ...bodyCell, textAlign: "right" }}>{money(v.oneday_rate)}</TableCell>
                      <TableCell sx={{ ...bodyCell, textAlign: "center" }}>{v.nights}</TableCell>
                      <TableCell sx={{ ...bodyCell, textAlign: "right" }}>{money(v.gross)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ID proofs of the selected visit (latest visit by default) */}
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: "bold", color: LABEL, mb: 0.5 }}>
                ID Proofs {current ? `- Room ${current.room_no}, ${current.check_in}` : ""}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", p: 1, border: `1px solid ${LINE}`, bgcolor: PANEL, minHeight: 96 }}>
                {current?.id_proofs?.length ? current.id_proofs.map((p, i) => (
                  <Box key={`${p}-${i}`} component="img" src={getImageUrl(p)} alt={`ID proof ${i + 1}`}
                    onClick={() => setPreview(getImageUrl(p))}
                    sx={{ height: 90, maxWidth: 150, objectFit: "contain", border: "1px solid #3a4a6b", cursor: "zoom-in", bgcolor: "#fff" }} />
                )) : (
                  <Typography sx={{ fontSize: "12px", color: MUTED, alignSelf: "center" }}>No ID proof uploaded</Typography>
                )}
              </Box>
            </Box>
          </>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "auto" }}>
          <Button onClick={onClose} sx={{ bgcolor: TEAL, color: "#fff", fontWeight: "bold", px: 3, "&:hover": { bgcolor: "#0a5f66" } }}>
            CLOSE
          </Button>
        </Box>
      </DialogContent>

      {/* Large ID proof */}
      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="lg">
        <Box sx={{ position: "relative", bgcolor: "#000" }}>
          <IconButton onClick={() => setPreview(null)} sx={{ position: "absolute", top: 4, right: 4, color: "#fff", bgcolor: "rgba(0,0,0,0.5)" }}>
            <CloseIcon />
          </IconButton>
          {preview && <Box component="img" src={preview} alt="ID proof" sx={{ display: "block", maxWidth: "90vw", maxHeight: "85vh" }} />}
        </Box>
      </Dialog>
    </Dialog>
  );
}
